import { AppConfig } from 'models/AppConfig';

import { getBrandAndPartnerKeys } from './constants/brandUrls';

const convertValue = (val: string) => {
  return val === 'true' ? true : val === 'false' ? false : val;
};

const getValueForBrand = (
  value: string | { [key: string]: string },
  brandKey: string,
): boolean | string | undefined => {
  if (typeof value === 'object' && value !== null) {
    return convertValue(value[brandKey]);
  }

  // If value is a string, try to parse it as JSON
  try {
    const parsedValue = JSON.parse(value as string);
    if (typeof parsedValue === 'object' && parsedValue !== null) {
      return convertValue(parsedValue[brandKey]);
    }
  } catch {
    // If parsing fails, treat as primitive value
    return convertValue(value);
  }

  // Fallback for primitive values
  return convertValue(value);
};

function getValue(
  envValue: string | { [key: string]: string },
  jsonValue: string | { [key: string]: string },
  brandKey: string,
) {
  return process.env.NODE_ENV !== 'production'
    ? getValueForBrand(envValue, brandKey)
    : getValueForBrand(jsonValue, brandKey);
}

export function buildConfig(jsonValue: any): AppConfig {
  const partnerDetails = getBrandAndPartnerKeys();
  const brandKey =
    partnerDetails?.brandKey || process.env.REACT_APP_BRAND_KEY || jsonValue.REACT_APP_BRAND_KEY || 'OMC';

  const partnerKey =
    partnerDetails?.partnerKey || process.env.REACT_APP_PARTNER_KEY || jsonValue.REACT_APP_PARTNER_KEY || 'ONE';

  return {
    api: {
      apiUrl: getValue(process.env.REACT_APP_API_URL || '', jsonValue.REACT_APP_API_URL || '', brandKey) as string,
      adminApiUrl: getValue(
        process.env.REACT_APP_ADMIN_API_URL || '',
        jsonValue.REACT_APP_ADMIN_API_URL || '',
        brandKey,
      ) as string,
      marketplaceApiUrl: getValue(
        process.env.REACT_APP_MARKETPLACE_API_URL || '',
        jsonValue.REACT_APP_MARKETPLACE_API_URL || '',
        brandKey,
      ) as string,
      publicApiUrl: getValue(
        process.env.REACT_APP_PUBLIC_API_URL || '',
        jsonValue.REACT_APP_PUBLIC_API_URL || '',
        brandKey,
      ) as string,
      solanaApiUrl: getValue(
        process.env.REACT_APP_SOLANA_RPC_API_URL || '',
        jsonValue.REACT_APP_SOLANA_RPC_API_URL || '',
        brandKey,
      ) as string,
      slapiApiUrl: getValue(
        process.env.REACT_APP_SLAPI_API_URL || '',
        jsonValue.REACT_APP_SLAPI_API_URL || '',
        brandKey,
      ) as string,
    },
    crypto: {
      splTokenProgramId: getValue(
        process.env.REACT_APP_SPL_TOKEN_PROGRAM_ID || '',
        jsonValue.REACT_APP_SPL_TOKEN_PROGRAM_ID || '',
        brandKey,
      ) as string,
      usdcTokenMint: getValue(
        process.env.REACT_APP_USDC_TOKEN_MINT || '',
        jsonValue.REACT_APP_USDC_TOKEN_MINT || '',
        brandKey,
      ) as string,
    },
    payment: {
      spreePayEnv: getValue(
        process.env.REACT_APP_SPREE_PAY_ENV || '',
        jsonValue.REACT_APP_SPREE_PAY_ENV || '',
        brandKey,
      ) as string,
      spreePayTenantId: getValue(
        process.env.REACT_APP_SPREE_PAY_TENANT_ID || '',
        jsonValue.REACT_APP_SPREE_PAY_TENANT_ID || '',
        brandKey,
      ) as string,
    },
    cms: {
      contentfulSpaceId: getValue(
        process.env.REACT_APP_CONTENTFUL_SPACE_ID || '',
        jsonValue.REACT_APP_CONTENTFUL_SPACE_ID || '',
        brandKey,
      ) as string,
      contentfulEnvironment: getValue(
        process.env.REACT_APP_CONTENTFUL_ENVIRONMENT || '',
        jsonValue.REACT_APP_CONTENTFUL_ENVIRONMENT || '',
        brandKey,
      ) as string,
      contentfulAccessToken: getValue(
        process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN || '',
        jsonValue.REACT_APP_CONTENTFUL_ACCESS_TOKEN || '',
        brandKey,
      ) as string,
    },
    appSettings: {
      //Partner and Brand keys will be used from env variables only for local development, otherwise it will be set base on brand hostname
      brandKey: brandKey,
      partnerKey: partnerKey,
      apiDomain: getValue(
        process.env.REACT_APP_API_DOMAIN || '',
        jsonValue.REACT_APP_API_DOMAIN || '',
        brandKey,
      ) as string,
      prodMode: getValue(
        process.env.REACT_APP_PROD_MODE || 'false',
        jsonValue.REACT_APP_PROD_MODE || 'false',
        brandKey,
      ) as boolean,
      isMaintenanceMode: getValue(
        process.env.REACT_APP_IS_MAINTENANCE_MODE || 'false',
        jsonValue.REACT_APP_IS_MAINTENANCE_MODE || 'false',
        brandKey,
      ) as boolean,
      applicationInsightsKey: getValue(
        process.env.REACT_APP_APPLICATION_INSIGHTS_IK || '',
        jsonValue.REACT_APP_APPLICATION_INSIGHTS_IK || '',
        brandKey,
      ) as string,
      applicationContextToken: getValue(
        process.env.REACT_APP_APPLICATION_CONTEXT_TOKEN || '',
        jsonValue.REACT_APP_APPLICATION_CONTEXT_TOKEN || '',
        brandKey,
      ) as string,
      publicUrl: getValue(
        process.env.REACT_APP_PUBLIC_URL || '',
        jsonValue.REACT_APP_PUBLIC_URL || '',
        brandKey,
      ) as string,
      enablePriceComparison: getValue(
        process.env.REACT_APP_ENABLE_PRICE_COMPARISON || 'false',
        jsonValue.REACT_APP_ENABLE_PRICE_COMPARISON || 'false',
        brandKey,
      ) as boolean,
      defaultStateToShip: getValue(
        process.env.REACT_APP_DEFAULT_STATE_TO_SHIP || 'CA',
        jsonValue.REACT_APP_DEFAULT_STATE_TO_SHIP || 'CA',
        brandKey,
      ) as string,
      materialUiLicense: getValue(
        process.env.REACT_APP_MATERIAL_UI_LICENSE || '',
        jsonValue.REACT_APP_MATERIAL_UI_LICENSE || '',
        brandKey,
      ) as string,
      hideCreateAccountBenefits: getValue(
        process.env.REACT_APP_HIDE_CREATE_ACCOUNT_BENEFITS || 'false',
        jsonValue.REACT_APP_HIDE_CREATE_ACCOUNT_BENEFITS || 'false',
        brandKey,
      ) as boolean,
      enableSocialLogins: getValue(
        process.env.REACT_APP_ENABLE_SOCIAL_LOGINS || 'false',
        jsonValue.REACT_APP_ENABLE_SOCIAL_LOGINS || 'false',
        brandKey,
      ) as boolean,
    },
    hubSpot: {
      hubspotPortalId: getValue(
        process.env.REACT_APP_HUBSPOT_PORTAL_ID || '',
        jsonValue.REACT_APP_HUBSPOT_PORTAL_ID || '',
        brandKey,
      ) as string,
      hubspotFormId: getValue(
        process.env.REACT_APP_HUBSPOT_FORM_ID || '',
        jsonValue.REACT_APP_HUBSPOT_FORM_ID || '',
        brandKey,
      ) as string,
      hubspotRegion: getValue(
        process.env.REACT_APP_HUBSPOT_REGION || '',
        jsonValue.REACT_APP_HUBSPOT_REGION || '',
        brandKey,
      ) as string,
      hubspotFormUrl: getValue(
        process.env.REACT_APP_HUBSPOT_FORM_URL || '',
        jsonValue.REACT_APP_HUBSPOT_FORM_URL || '',
        brandKey,
      ) as string,
    },
    google: {
      googleMapsKey: getValue(
        process.env.REACT_APP_GOOGLE_MAPS_KEY || '',
        jsonValue.REACT_APP_GOOGLE_MAPS_KEY || '',
        brandKey,
      ) as string,
      geolocationUrl: getValue(
        process.env.REACT_APP_GEOLOCATION_URL || '',
        jsonValue.REACT_APP_GEOLOCATION_URL || '',
        brandKey,
      ) as string,
      geolocationKey: getValue(
        process.env.REACT_APP_GEOLOCATION_KEY || '',
        jsonValue.REACT_APP_GEOLOCATION_KEY || '',
        brandKey,
      ) as string,
    },
    assets: {
      sasToken: getValue(
        process.env.REACT_APP_ASSETS_SAS_TOKEN || '',
        jsonValue.REACT_APP_ASSETS_SAS_TOKEN || '',
        brandKey,
      ) as string,
    },
    analytics: {
      hubspotUrl: getValue(
        process.env.REACT_APP_HUBSPOT_URL || '',
        jsonValue.REACT_APP_HUBSPOT_URL || '',
        brandKey,
      ) as string,
      gaTrackingKey: getValue(
        process.env.REACT_APP_GA_TRACKING_ID || '',
        jsonValue.REACT_APP_GA_TRACKING_ID || '',
        brandKey,
      ) as string,
      gaPartnerTrackingKey: getValue(
        process.env.REACT_APP_GA_PARTNER_TRACKING_ID || '',
        jsonValue.REACT_APP_GA_PARTNER_TRACKING_ID || '',
        brandKey,
      ) as string,
      crazyEggUrl: getValue(
        process.env.REACT_APP_CRAZY_EGG_URL || '',
        jsonValue.REACT_APP_CRAZY_EGG_URL || '',
        brandKey,
      ) as string,
    },
    princessConfig: {
      iframeSignInUrl: getValue(
        process.env.REACT_APP_PRINCESS_IFRAME_SIGNIN_URL || '',
        jsonValue.REACT_APP_PRINCESS_IFRAME_SIGNIN_URL || '',
        brandKey,
      ) as string,
      iframeSignInEnabled: getValue(
        process.env.REACT_APP_PRINCESS_IFRAME_SIGNIN_ENABLED || 'false',
        jsonValue.REACT_APP_PRINCESS_IFRAME_SIGNIN_ENABLED || 'false',
        brandKey,
      ) as boolean,
      isSingleIdentityEnabled: getValue(
        process.env.REACT_APP_ENABLE_MCM_SINGLE_IDENTITY || 'false',
        jsonValue.REACT_APP_ENABLE_MCM_SINGLE_IDENTITY || 'false',
        brandKey,
      ) as boolean,
    },
    widgetConfig: {
      surflyKey: getValue(
        process.env.REACT_APP_SURFLY_KEY || '',
        jsonValue.REACT_APP_SURFLY_KEY || '',
        brandKey,
      ) as string,
    },
  };
}
